/**
 * useSphereAttachments.ts
 *
 * Manages sphere-attachment list state for a single sphere. Fetches the
 * collection on mount / on sphereId change, exposes optimistic `addFile`,
 * `addLink`, `updateLink`, and `remove` mutations, and re-syncs from the
 * server on error to keep the UI consistent with authoritative state.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useApiClientsOrNull } from 'design-system/my-lib';
import type { SphereAttachment } from 'types/attachment';
import {
  createLinkAttachment,
  deleteSphereAttachment,
  listSphereAttachments,
  updateLinkAttachment,
  uploadFileAttachment,
} from '../api/attachmentsApi';
import type { UpdateLinkAttachmentPayload } from '../api/attachmentsApi';

const DEFAULT_PAGE_SIZE = 50;

/**
 * Polling cadence used while any attachment is `scan_status='pending'`. The
 * UI shows a "scanning…" badge for those rows; the spec calls for polling
 * the metadata endpoint until status flips clean (or the row is removed
 * because the verdict was malicious). 8s tick × 15 max ticks = ~2 min cap,
 * which comfortably outruns Defender for Storage's typical scan latency
 * (seconds). Beyond the cap we stop polling silently — the user can close /
 * reopen the editor to retry, and the stuck-row janitor (deferred MVP item)
 * eventually closes out anything truly hung.
 */
export const SCAN_POLL_INTERVAL_MS = 8_000;
export const SCAN_POLL_MAX_TICKS = 15;

export interface UseSphereAttachmentsOptions {
  sphereId: string | undefined;
  pageSize?: number;
}

export interface UseSphereAttachmentsResult {
  attachments: SphereAttachment[];
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  addFile: (file: File, description?: string) => Promise<SphereAttachment>;
  addLink: (payload: {
    url: string;
    name?: string;
    description?: string | null;
  }) => Promise<SphereAttachment>;
  updateLink: (
    attachmentId: string,
    payload: UpdateLinkAttachmentPayload
  ) => Promise<SphereAttachment>;
  remove: (attachmentId: string) => Promise<void>;
}

function toErrorMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (typeof err === 'string') return err;
  return 'Unknown error';
}

export function useSphereAttachments({
  sphereId,
  pageSize = DEFAULT_PAGE_SIZE,
}: UseSphereAttachmentsOptions): UseSphereAttachmentsResult {
  const apiClients = useApiClientsOrNull();
  const graphClient = apiClients?.graphClient ?? null;

  const [attachments, setAttachments] = useState<SphereAttachment[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const requestIdRef = useRef(0);

  // Pin graphClient in a ref so callback identities don't churn every time the
  // ApiClients context emits a new object. Without this, parent re-renders
  // (formik state, schema fetches, etc.) cascade into the refresh callback's
  // dep array → the list-effect re-fires → duplicate network fetches per
  // editor open. We still need to react when graphClient transitions
  // null → non-null on first auth, so the effect uses a `graphClientReady`
  // boolean instead of the client object itself.
  const graphClientRef = useRef(graphClient);
  graphClientRef.current = graphClient;
  const graphClientReady = graphClient !== null;

  const refresh = useCallback(async (): Promise<void> => {
    const client = graphClientRef.current;
    if (!client || !sphereId) {
      setAttachments([]);
      setIsLoading(false);
      setError(null);
      return;
    }
    const requestId = ++requestIdRef.current;
    setIsLoading(true);
    setError(null);
    try {
      const response = await listSphereAttachments(client, sphereId, {
        limit: pageSize,
      });
      if (requestId !== requestIdRef.current) return; // stale fetch
      setAttachments(response.attachments ?? []);
    } catch (err: unknown) {
      if (requestId !== requestIdRef.current) return;
      console.error('[useSphereAttachments] Failed to list attachments:', err);
      setError(toErrorMessage(err));
    } finally {
      if (requestId === requestIdRef.current) {
        setIsLoading(false);
      }
    }
  }, [sphereId, pageSize]);

  useEffect(() => {
    refresh().catch(() => undefined);
    // graphClientReady is included so the first fetch fires after auth resolves
    // (null → non-null transition). Subsequent identity churn on graphClient
    // does not flip the boolean, so the effect stays quiet.
  }, [refresh, graphClientReady]);

  // Poll while any attachment is awaiting an AV verdict. Boolean dep (vs
  // the full attachments array) keeps the effect quiet across refreshes
  // that don't change the pending/clean composition — the same interval
  // and counter persist across status-quo refreshes, so the cap reflects
  // wall-clock polls and not effect-restart count.
  const hasPendingAttachment = attachments.some(
    (a) => a.scan_status === 'pending',
  );

  useEffect(() => {
    if (!hasPendingAttachment) return undefined;

    let tickCount = 0;
    const intervalId = setInterval(() => {
      tickCount += 1;
      if (tickCount > SCAN_POLL_MAX_TICKS) {
        clearInterval(intervalId);
        return;
      }
      refresh().catch(() => undefined);
    }, SCAN_POLL_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [hasPendingAttachment, refresh]);

  const addFile = useCallback(
    async (file: File, description?: string): Promise<SphereAttachment> => {
      const client = graphClientRef.current;
      if (!client || !sphereId) {
        throw new Error('Api clients or sphereId unavailable');
      }
      try {
        const created = await uploadFileAttachment(
          client,
          sphereId,
          file,
          description
        );
        // Prepend so most-recent-first ordering matches server.
        setAttachments((prev) => [created, ...prev.filter((a) => a.id !== created.id)]);
        return created;
      } catch (err: unknown) {
        console.error('[useSphereAttachments] addFile failed:', err);
        // Re-sync so partial state from a failed upload does not stick.
        await refresh();
        throw err;
      }
    },
    [sphereId, refresh]
  );

  const addLink = useCallback(
    async (payload: {
      url: string;
      name?: string;
      description?: string | null;
    }): Promise<SphereAttachment> => {
      const client = graphClientRef.current;
      if (!client || !sphereId) {
        throw new Error('Api clients or sphereId unavailable');
      }
      try {
        const created = await createLinkAttachment(client, sphereId, payload);
        setAttachments((prev) => [created, ...prev.filter((a) => a.id !== created.id)]);
        return created;
      } catch (err: unknown) {
        console.error('[useSphereAttachments] addLink failed:', err);
        await refresh();
        throw err;
      }
    },
    [sphereId, refresh]
  );

  const updateLink = useCallback(
    async (
      attachmentId: string,
      payload: UpdateLinkAttachmentPayload
    ): Promise<SphereAttachment> => {
      const client = graphClientRef.current;
      if (!client || !sphereId) {
        throw new Error('Api clients or sphereId unavailable');
      }
      let snapshot: SphereAttachment | null = null;
      let snapshotIndex = -1;
      setAttachments((prev) => {
        const idx = prev.findIndex((a) => a.id === attachmentId);
        if (idx === -1) return prev;
        snapshot = prev[idx];
        snapshotIndex = idx;
        const optimistic: SphereAttachment = {
          ...prev[idx],
          ...(payload.url === undefined ? {} : { url: payload.url }),
          ...(payload.name === undefined ? {} : { name: payload.name }),
          ...(payload.description === undefined
            ? {}
            : { description: payload.description }),
        };
        const next = prev.slice();
        next[idx] = optimistic;
        return next;
      });
      // Invalidate any in-flight refresh so its result cannot clobber the
      // optimistic row. New refreshes started after this point will win.
      ++requestIdRef.current;
      try {
        const updated = await updateLinkAttachment(
          client,
          sphereId,
          attachmentId,
          payload
        );
        setAttachments((prev) => {
          const idx = prev.findIndex((a) => a.id === attachmentId);
          if (idx === -1) return prev;
          const next = prev.slice();
          next[idx] = updated;
          return next;
        });
        return updated;
      } catch (err: unknown) {
        console.error('[useSphereAttachments] updateLink failed:', err);
        if (snapshot && snapshotIndex !== -1) {
          const restored = snapshot;
          const restoredIndex = snapshotIndex;
          setAttachments((prev) => {
            const idx = prev.findIndex((a) => a.id === attachmentId);
            if (idx !== -1) {
              const next = prev.slice();
              next[idx] = restored;
              return next;
            }
            const next = prev.slice();
            next.splice(restoredIndex, 0, restored);
            return next;
          });
        }
        await refresh();
        throw err;
      }
    },
    [sphereId, refresh]
  );

  const remove = useCallback(
    async (attachmentId: string): Promise<void> => {
      const client = graphClientRef.current;
      if (!client || !sphereId) {
        throw new Error('Api clients or sphereId unavailable');
      }
      // Optimistic removal; snapshot in case we need to roll back.
      let snapshot: SphereAttachment[] = [];
      setAttachments((prev) => {
        snapshot = prev;
        return prev.filter((a) => a.id !== attachmentId);
      });
      try {
        await deleteSphereAttachment(client, sphereId, attachmentId);
      } catch (err: unknown) {
        console.error('[useSphereAttachments] remove failed:', err);
        // Roll back optimistic removal, then re-sync to pick up any
        // server-side changes that happened in the meantime.
        setAttachments(snapshot);
        await refresh();
        throw err;
      }
    },
    [sphereId, refresh]
  );

  return {
    attachments,
    isLoading,
    error,
    refresh,
    addFile,
    addLink,
    updateLink,
    remove,
  };
}
